import React, { useState, useEffect } from 'react';
import { Key, Save, Eye, EyeOff } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { toast } from 'sonner';
import { apiKeysAPI } from '../../services/api';

const APIKeysSettings = () => {
  const [keys, setKeys] = useState({
    openrouter_key: '',
    gemini_key: '',
    use_openrouter: false,
  });
  const [showKeys, setShowKeys] = useState({
    openrouter: false,
    gemini: false,
  });
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    loadAPIKeys();
  }, []);

  const loadAPIKeys = async () => {
    setIsLoading(true);
    try {
      const response = await apiKeysAPI.get();
      setKeys(response.data);
    } catch (error) {
      toast.error('Erreur lors du chargement des clés');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await apiKeysAPI.update(keys);
      toast.success('Clés API mises à jour avec succès');
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="glass rounded-2xl p-8 animate-pulse">
        <div className="h-64 bg-slate-700/30 rounded-lg" />
      </div>
    );
  }

  return (
    <div className="glass rounded-2xl p-6 shadow-xl border border-slate-700/50 animate-fadeIn">
      <div className="flex items-center gap-3 mb-6">
        <Key className="w-6 h-6 text-blue-400" />
        <h2 className="text-2xl font-bold text-white">Configuration des Clés API</h2>
      </div>

      <div className="space-y-6">
        {/* Gemini Flash (Gratuit) */}
        <div className="bg-slate-700/30 rounded-lg p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-white">Gemini Flash 1.5</h3>
            <span className="px-3 py-1 bg-green-500/20 text-green-400 rounded-full text-sm">Gratuit</span>
          </div>
          <p className="text-gray-400 text-sm mb-4">
            Modèle gratuit de Google. Utilisez votre propre clé ou laissez vide pour utiliser la clé par défaut.
          </p>
          <div className="relative">
            <Input
              type={showKeys.gemini ? 'text' : 'password'}
              value={keys.gemini_key || ''}
              onChange={(e) => setKeys({ ...keys, gemini_key: e.target.value })}
              placeholder="Clé API Gemini (optionnel)"
              className="bg-slate-800 border-slate-600 text-white pr-12"
              data-testid="gemini-key-input"
            />
            <button
              type="button"
              onClick={() => setShowKeys({ ...showKeys, gemini: !showKeys.gemini })}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white"
            >
              {showKeys.gemini ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
            </button>
          </div>
        </div>

        {/* OpenRouter */}
        <div className="bg-slate-700/30 rounded-lg p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-white">OpenRouter</h3>
            <span className="px-3 py-1 bg-blue-500/20 text-blue-400 rounded-full text-sm">Premium</span>
          </div>
          <p className="text-gray-400 text-sm mb-4">
            Accès à plusieurs modèles premium (GPT-4, Claude, etc.). Obtenez votre clé sur{' '}
            <a href="https://openrouter.ai" target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:text-blue-300">
              openrouter.ai
            </a>
          </p>
          <div className="relative mb-4">
            <Input
              type={showKeys.openrouter ? 'text' : 'password'}
              value={keys.openrouter_key || ''}
              onChange={(e) => setKeys({ ...keys, openrouter_key: e.target.value })}
              placeholder="Clé API OpenRouter"
              className="bg-slate-800 border-slate-600 text-white pr-12"
              data-testid="openrouter-key-input"
            />
            <button
              type="button"
              onClick={() => setShowKeys({ ...showKeys, openrouter: !showKeys.openrouter })}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white"
            >
              {showKeys.openrouter ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
            </button>
          </div>
          <div className="flex items-center gap-3">
            <input
              type="checkbox"
              checked={keys.use_openrouter}
              onChange={(e) => setKeys({ ...keys, use_openrouter: e.target.checked })}
              className="w-4 h-4"
              data-testid="use-openrouter-switch"
            />
            <label className="text-sm text-gray-300">
              Utiliser OpenRouter par défaut pour la génération
            </label>
          </div>
        </div>

        {/* Info Box */}
        <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4">
          <p className="text-sm text-blue-300">
            <strong>Note:</strong> Si aucune clé n'est fournie, une clé par défaut sera utilisée pour Gemini Flash (gratuit).
          </p>
        </div>

        {/* Save Button */}
        <Button
          onClick={handleSave}
          disabled={isSaving}
          className="w-full bg-blue-600 hover:bg-blue-700"
          data-testid="save-api-keys-btn"
        >
          <Save className="w-4 h-4 mr-2" />
          {isSaving ? 'Sauvegarde...' : 'Sauvegarder les Clés'}
        </Button>
      </div>
    </div>
  );
};

export default APIKeysSettings;
